import Cookies from "js-cookie";
import { useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom"
import useAuth from "../hook/useAuth";
import baseURL from "../config/api";
import { jwtDecode } from "jwt-decode";
import { getCourseCategoriesWithPublishCourses } from "../services/Course";
import useSearch from "../hook/useSearch";


export const Navbar = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { isSearchFocused, setIsSearchFocused, showMobileSearch, setShowMobileSearch, searchValue, setSearchValue }: any = useSearch();
    const token = Cookies.get("token-elministry");
    const profileImg = Cookies.get("profileImg-elministry");
    const { auth, setAuth }: any = useAuth();
    const [authIsFound, setAuthIsFound] = useState<boolean>(false);
    const [isExploreOpen, setIsExploreOpen] = useState(false);
    const [selectedExploreCourse, setSelectedExploreCourse] = useState<number | null>(null);
    const [isSidebarOpen, setSidebarOpen] = useState(false);
    const [openIndexes, setOpenIndexes] = useState<number[]>([]);
    const [hideMenuDropDown, setHideMenuDropDown] = useState<boolean>(false);
    const [isRoleDropdownOpen, setIsRoleDropdownOpen] = useState<boolean>(false);
    const [exploreList, setExploreList] = useState<any[]>([]);
    const [exploreDataLoaded, setExploreDataLoaded] = useState<boolean>(false);
    const exploreDropdownRef = useRef<HTMLDivElement | null>(null);
    const dropDownVisibleRef = useRef<HTMLDivElement | null>(null);
    const hideHeaderForDoc = "docs";
    const hideHeaderForHelp = "help";

    useEffect(() => {
        const handleResize = () => {
            setSidebarOpen(false);
        }

        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (isSidebarOpen) {
            document.body.classList.add("overflow-hidden");
        } else {
            document.body.classList.remove("overflow-hidden");
        }

        return () => document.body.classList.remove("overflow-hidden");
    }, [isSidebarOpen]);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            const clickedTarget = event.target as Node;

            if (exploreDropdownRef.current && !exploreDropdownRef.current.contains(clickedTarget)) {
                setIsExploreOpen(false);
            }

            if (dropDownVisibleRef.current && !dropDownVisibleRef.current.contains(clickedTarget)) {
                setHideMenuDropDown(false);
            }
        }

        document.addEventListener("click", handleClickOutside);
        return () => {
            document.removeEventListener("click", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (token && !authIsFound) {
            const jsonData: any = jwtDecode(token);
            const id = jsonData["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"];
            const role = jsonData["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"];
            const user = jsonData["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name"];
            const givenname = jsonData["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname"];
            const emailAddress = jsonData["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress"];
            setAuth({ id, user, givenname, emailAddress, role, token, profileImage: profileImg });
            setAuthIsFound(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (isExploreOpen || isSidebarOpen) {
            setExploreDataLoaded(false);
            getCourseCategoriesWithPublishCourses()
                .then((data) => {
                    const list = data?.filter((category: any) => category?.courses?.length > 0)?.map((category: any) => {
                        return {
                            ...category,
                            courses: category?.courses?.map((item: any) => {
                                return {
                                    ...item,
                                }
                            })
                        }
                    })

                    setExploreList(list);
                    setExploreDataLoaded(true);
                })
                .catch((error) => {
                    setExploreList([]);
                    setExploreDataLoaded(true);
                    console.error(error)
                })
        }
    }, [isExploreOpen, isSidebarOpen])

    const toggleAccordion = (index: number) => {
        setOpenIndexes((prev) =>
            prev.includes(index)
                ? prev.filter((i) => i !== index) // close if already open
                : [...prev, index] // open new along with existing
        );
    };

    const navigateToDashboard = () => {
        setHideMenuDropDown(false);
        let navigationLink = "";
        switch (auth?.role) {
            case "Admin":
                navigationLink = "/admin/users";
                break;
            case "Contributor":
                navigationLink = "/contributor/dashboard";
                break;

            case "Teacher":
                navigationLink = "/teacher/dashboard";
                break;

            case "Parent":
                navigationLink = "/parent/dashboard";
                break;

            case "Learner":
                navigationLink = "/learner/dashboard";
                break;
        }

        navigate(navigationLink);
    }

    const logOut = () => {
        setHideMenuDropDown(false);
        setAuth(null);
        Cookies.remove("token-elministry");
        Cookies.remove("role-elministry");
        Cookies.remove("userId-elministry");
        Cookies.remove("profileImg-elministry");
        navigate("/login");
    }

    const shouldHideHeader =
        location.pathname.replace("/", "").includes(hideHeaderForDoc) ||
        location.pathname.replace("/", "").includes(hideHeaderForHelp);

    if (shouldHideHeader) {
        return null;
    }

    return (
        <>
            <nav className="sticky top-0 z-[9999] bg-white shadow-sm">
                <div className="w-full px-3 py-2 flex items-center justify-between">
                    {
                        !showMobileSearch && (
                            <div className="flex items-center gap-4 w-1/2">
                                <div>
                                    <NavLink to="/">
                                        <img className="logo-img w-[65px]" src="/images/logo-backgroundless.png" alt="logo" />
                                    </NavLink>
                                </div>

                                <div className={`flex items-center w-full hover:border-primary ${isSearchFocused ? "border-2 border-primary" : "border border-gray-300"} rounded-[22px] px-2 py-[6px] max-md:hidden`}>
                                    <input
                                        type="text"
                                        placeholder="Search..."
                                        className="flex-grow outline-none px-2 text-sm"
                                        onFocus={() => setIsSearchFocused(true)}
                                        onBlur={() => setIsSearchFocused(false)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" && searchValue.trim() !== "") {
                                                navigate("search/result")
                                            }
                                        }}
                                        value={searchValue}
                                        onChange={(e) => setSearchValue(e.target.value)}
                                    />

                                    {
                                        searchValue && (
                                            <button
                                                onClick={() => setSearchValue("")}
                                                className="text-black mx-2"
                                            >
                                                <span className="material-symbols-outlined text-lg">
                                                    close
                                                </span>
                                            </button>
                                        )
                                    }

                                    <button className="text-white px-[6px] rounded-full bg-primary shadow-md"
                                        onClick={() => {
                                            if (searchValue.trim() !== "") {
                                                navigate("search/result")
                                            }

                                        }}
                                    >
                                        <span className="material-symbols-outlined text-lg">
                                            search
                                        </span>
                                    </button>
                                </div>

                                <div className="max-md:hidden relative">
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setIsExploreOpen(!isExploreOpen);
                                            setSelectedExploreCourse(null);
                                        }}
                                        className="border-2 border-primary text-primary px-3 py-1 rounded-[20px] text-sm flex items-center gap-1"
                                    >
                                        Explore <span className="material-symbols-outlined text-lg">
                                            {
                                                isExploreOpen ? (
                                                    "keyboard_arrow_up"
                                                ) : (
                                                    "keyboard_arrow_down"
                                                )
                                            }
                                        </span>
                                    </button>

                                    {
                                        isExploreOpen && (

                                            <div ref={exploreDropdownRef} className="absolute left-0 top-[50px] w-max bg-white shadow-lg rounded border z-[1000] flex overflow-hidden max-md:hidden">

                                                {
                                                    (exploreDataLoaded && exploreList.length > 0) && (
                                                        <>
                                                            {/* Left Sidebar */}
                                                            <ul className="w-64 px-2 pt-2 pb-4 max-h-[504px] border-r overflow-y-auto">
                                                                {exploreList?.map((item, index) => (
                                                                    <li
                                                                        key={index}
                                                                        onClick={() => setSelectedExploreCourse(index)}
                                                                        className="group"
                                                                    >
                                                                        <div className="flex justify-between items-center px-4 py-1 hover:bg-blue-100 cursor-pointer rounded">
                                                                            <span className="font-medium uppercase text-[16px] text-gray-800 hover:underline">{item?.name}</span>

                                                                            <span className="material-symbols-outlined text-2xl text-gray-800">
                                                                                chevron_right
                                                                            </span>

                                                                        </div>
                                                                    </li>
                                                                ))}
                                                            </ul>

                                                            {/* Right Content */}
                                                            <div className="px-2 pt-2 pb-4 max-h-[504px] overflow-y-auto">
                                                                {selectedExploreCourse !== null && exploreList[selectedExploreCourse]?.courses?.length ? (
                                                                    <ul>
                                                                        {exploreList[selectedExploreCourse].courses?.map((course: any, idx: number) => (
                                                                            <li key={idx} className="mb-3 w-full text-gray-800 rounded px-4 py-1 hover:underline hover:bg-blue-100 hover:text-primary cursor-pointer"
                                                                                onClick={() => {
                                                                                    setIsExploreOpen(false)
                                                                                    setSelectedExploreCourse(null);
                                                                                }}
                                                                            >
                                                                                <NavLink
                                                                                    to="/course-lesson"
                                                                                    state={{
                                                                                        courseId: course.id,
                                                                                        lessonId: null,
                                                                                        isLogin: token ? true : false,
                                                                                        isLearner: auth?.role === "Learner" ? true : false,
                                                                                    }}
                                                                                    className="font-medium uppercase text-[16px] inline-block w-full"
                                                                                >
                                                                                    {course?.title}
                                                                                </NavLink>
                                                                            </li>
                                                                        ))}
                                                                    </ul>
                                                                ) : (
                                                                    <div className="h-full flex items-center justify-center">
                                                                        <p className="text-gray-500 text-sm">Select a category to view its courses</p>
                                                                    </div>

                                                                )}
                                                            </div>
                                                        </>
                                                    )
                                                }

                                                {
                                                    !exploreDataLoaded && (
                                                        <div className="flex items-center justify-center w-64 p-2">
                                                            <div className="mb-4 h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-primary" />
                                                        </div>
                                                    )
                                                }

                                                {
                                                    (exploreDataLoaded && exploreList.length === 0) && (
                                                        <div className="flex items-center justify-center w-64 p-2">
                                                            <span className="text-lg text-gray-400">No course found!</span>
                                                        </div>
                                                    )
                                                }

                                            </div>

                                        )
                                    }
                                </div>
                            </div>
                        )
                    }


                    <div className={`max-md:flex max-md:items-center max-md:gap-4 hidden relative ${showMobileSearch ? "w-full" : "w-max"}`}>
                        <button
                            onClick={() => {
                                setShowMobileSearch(!showMobileSearch)
                                setSearchValue("");
                            }}
                            className="text-primary flex items-center justify-center"
                        >
                            <span className="material-symbols-outlined text-3xl">
                                {showMobileSearch ? "close" : "search"}
                            </span>
                        </button>

                        <div className={`flex items-center ${showMobileSearch ? "w-full" : "w-max"}`}>

                            <div
                                className={`flex items-center py-2 overflow-hidden transition-all duration-300 ml-2 ${showMobileSearch ? "w-full sm:w-full opacity-100" : "w-0 opacity-0"
                                    }`}
                            >
                                <input
                                    type="text"
                                    placeholder="Search..."
                                    className="flex-grow outline-none border border-gray-300 rounded-md px-3 py-[4px] text-sm transition-all duration-300 w-full h-10 focus:border-primary focus:border-2"
                                    value={searchValue}
                                    onChange={(e) => setSearchValue(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" && searchValue.trim() !== "") {
                                            navigate("search/result");

                                        }
                                    }}
                                />

                            </div>
                        </div>
                        <div className="md:hidden">
                            {
                                isSidebarOpen ? (
                                    <button
                                        onClick={() => setSidebarOpen(false)}
                                        className="text-primary"
                                    >
                                        <span className="material-symbols-outlined text-3xl">
                                            close
                                        </span>
                                    </button>
                                ) : (
                                    <button
                                        onClick={() => {
                                            if (showMobileSearch) {
                                                if (searchValue.trim() !== "") {
                                                    navigate("search/result");

                                                }
                                            }
                                            else {
                                                setSidebarOpen(true)
                                            }
                                        }
                                        }
                                        className="text-primary"
                                    >
                                        <span className="material-symbols-outlined text-3xl">
                                            {showMobileSearch ? "search" : "menu"}
                                        </span>
                                    </button>
                                )
                            }
                        </div>
                    </div>

                    <div className="flex items-center gap-6 max-md:hidden">
                        <NavLink
                            to="/"
                            className={({ isActive }) =>
                                `text-sm hover:text-primary ${isActive ? "text-primary font-semibold" : "text-gray-500"}`
                            }
                        >
                            Home
                        </NavLink>
                        <NavLink
                            to="about-us"
                            className={({ isActive }) =>
                                `text-sm hover:text-primary ${isActive ? "text-primary font-semibold" : "text-gray-500"}`
                            }
                        >
                            About Us
                        </NavLink>
                        <NavLink
                            to="features"
                            className={({ isActive }) =>
                                `text-sm hover:text-primary ${isActive ? "text-primary font-semibold" : "text-gray-500"}`
                            }
                        >
                            Features
                        </NavLink>
                        <NavLink
                            to="blogs"
                            className={({ isActive }) =>
                                `text-sm hover:text-primary ${isActive ? "text-primary font-semibold" : "text-gray-500"}`
                            }
                        >
                            Blogs
                        </NavLink>
                        <NavLink
                            to="faq"
                            className={({ isActive }) =>
                                `text-sm hover:text-primary ${isActive ? "text-primary font-semibold" : "text-gray-500"}`
                            }
                        >
                            FAQ
                        </NavLink>
                        {
                            token
                                ? (
                                    <div ref={dropDownVisibleRef}>
                                        <div
                                            className="flex gap-1.5 cursor-pointer"
                                            onClick={() => setHideMenuDropDown(!hideMenuDropDown)}
                                        >
                                            {auth?.profileImage !== "" && auth?.profileImage !== "null" && auth?.profileImage !== "string" ? (

                                                <img
                                                    className="w-[40px] h-[40px] rounded-full shadow-md"
                                                    alt="profileImg"
                                                    src={`${baseURL}commonFile/Get?name=${auth?.profileImage}`}
                                                    onError={({ currentTarget }) => {
                                                        currentTarget.onerror = null;
                                                        currentTarget.src = "./images/profile-placeholder.png";
                                                    }}
                                                />

                                            ) : (

                                                <img src="./images/profile-placeholder.png" alt="profile_placeholder" className="w-[40px] h-[40px] rounded-full shadow-md" />

                                            )}

                                            <button
                                                className="text-primary p-2 rounded-[20px] text-sm flex items-center gap-x-[0.75rem]"

                                            >
                                                <span className="font-medium">{auth?.givenname}</span>
                                                <span className="material-symbols-outlined text-lg">
                                                    {
                                                        hideMenuDropDown ? (
                                                            "keyboard_arrow_up"
                                                        )
                                                            :
                                                            (
                                                                "keyboard_arrow_down"
                                                            )
                                                    }
                                                </span>
                                            </button>
                                        </div>


                                        {hideMenuDropDown && (
                                            <div className="absolute right-[0.125rem] bg-white shadow-lg border rounded-md">
                                                <ul className="min-w-44 w-max text-sm text-gray-700 p-2 space-y-3">
                                                    <li
                                                        className="hover:bg-gray-100 hover:underline cursor-pointer text-nowrap"
                                                        onClick={navigateToDashboard}
                                                    >
                                                        {
                                                            auth?.role === "Admin" ? "Admin Page" : `${auth?.role} Dashboard`
                                                        }

                                                    </li>

                                                    <li
                                                        className="hover:bg-gray-100 hover:underline cursor-pointer"
                                                        onClick={() => {
                                                            setHideMenuDropDown(false);
                                                            navigate("/settings")
                                                        }}
                                                    >
                                                        Settings
                                                    </li>

                                                    <li
                                                        className="hover:bg-gray-100 hover:underline cursor-pointer"
                                                        onClick={logOut}
                                                    >
                                                        Logout
                                                    </li>
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <>
                                        <button
                                            className="border-2 border-primary text-primary px-6 py-2 rounded-[20px] text-sm hover:shadow-md"
                                            onClick={() => navigate('/login')}
                                        >
                                            Log in
                                        </button>

                                        <button
                                            className="bg-primary text-white px-6 py-[10px] rounded-[20px] text-sm hover:shadow-md"
                                            onClick={() => navigate('/signup')}
                                        >
                                            Sign up
                                        </button>
                                    </>
                                )
                        }
                    </div>
                </div>
            </nav>

            <div className={`fixed top-[80px] left-0 w-full overflow-auto h-screen bg-white z-50 shadow-lg transform transition-transform duration-300 ease-in-out ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"}`}>

                {/* Accordion List */}
                <div className="pt-2 pb-10 max-md:h-[calc(100vh-200px)] overflow-auto">
                    {
                        (exploreDataLoaded && exploreList.length > 0) && (
                            exploreList?.map((item: any, index: number) => {
                                const isOpen = openIndexes?.includes(index);

                                return (
                                    <div key={index} className="border-b-2 border-[#D0D0D0]">
                                        {/* Accordion Header */}
                                        <button
                                            onClick={() => toggleAccordion(index)}
                                            className={`w-full bg-white flex gap-1.5 items-center uppercase p-2 text-lg text-left text-black font-semibold hover:bg-gray-100 ${isOpen ? "border border-primary" : ""
                                                }`}
                                        >
                                            <span className="material-symbols-outlined text-lg">
                                                {isOpen ? "keyboard_arrow_up" : "keyboard_arrow_down"}
                                            </span>
                                            {item.name}
                                        </button>

                                        <div
                                            className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                                                }`}
                                        >
                                            <div className="overflow-hidden">
                                                <ul className="p-6 space-y-4 text-lg">
                                                    {item?.courses?.map((course: any, i: number) => (
                                                        <li key={i} onClick={() => {
                                                            setSidebarOpen(false)
                                                            setOpenIndexes([]);
                                                        }}>
                                                            <NavLink
                                                                to="/course-lesson"
                                                                state={{
                                                                    courseId: course.id,
                                                                    lessonId: null,
                                                                    isLogin: token ? true : false,
                                                                    isLearner: auth?.role === "Learner" ? true : false,
                                                                }}
                                                                className="block text-primary transition"
                                                            >
                                                                {course.title}
                                                            </NavLink>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                )
                            })
                        )
                    }


                    {
                        !exploreDataLoaded && (
                            <div className="flex items-center justify-center w-full p-2">
                                <div className="mb-4 h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-primary" />
                            </div>
                        )
                    }

                    {
                        token && (
                            <div className="border-b-2 border-[#D0D0D0]">
                                {/* Accordion Header */}
                                <button
                                    onClick={() => setIsRoleDropdownOpen(!isRoleDropdownOpen)}
                                    className={`w-full bg-white flex gap-1.5 items-center p-2 text-lg text-left text-black font-semibold hover:bg-gray-100 ${isRoleDropdownOpen ? "border border-primary" : ""
                                        }`}
                                >
                                    <span className="material-symbols-outlined text-lg">
                                        {isRoleDropdownOpen ? "keyboard_arrow_up" : "keyboard_arrow_down"}
                                    </span>

                                    {auth?.givenname}
                                </button>

                                <div className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${isRoleDropdownOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
                                    <div className="overflow-hidden">
                                        <ul className="p-6 space-y-4 text-lg">

                                            <li
                                                className="block text-primary transition"
                                                onClick={() => {
                                                    setIsRoleDropdownOpen(false);
                                                    navigateToDashboard();
                                                    setSidebarOpen(false);
                                                }}
                                            >
                                                {
                                                    auth?.role === "Admin" ? "Admin Page" : `${auth?.role} Dashboard`
                                                }
                                            </li>

                                            <li
                                                className="block text-primary transition"
                                                onClick={() => {
                                                    setIsRoleDropdownOpen(false);
                                                    navigate("/settings")
                                                    setSidebarOpen(false);
                                                }}
                                            >
                                                Settings
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        )
                    }
                </div>
                {
                    token ? (
                        <div className="py-4">
                            <div className="flex justify-center">
                                <button
                                    className={`w-[49%] border-2 border-primary text-primary px-6 py-2 rounded-[20px] text-sm`}
                                    onClick={() => {
                                        setSidebarOpen(false);
                                        logOut();
                                    }}
                                >
                                    Log out
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="flex justify-between items-center p-2 border-b">
                            <button
                                className={`w-[49%] border-2 border-primary text-primary px-6 py-2 rounded-[20px] text-sm`}
                                onClick={() => {
                                    setSidebarOpen(false);
                                    navigate('/login')
                                }}
                            >
                                Log in
                            </button>

                            <button
                                type="button"
                                className={`w-[49%] bg-primary text-white px-6 py-[10px] rounded-[20px] text-sm`}
                                onClick={() => {
                                    setSidebarOpen(false);
                                    navigate('/signup')
                                }}
                            >
                                Sign up
                            </button>
                        </div>
                    )
                }
            </div>
        </>
    )
}